import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronUp, ChevronDown, Clock, Flame } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix image URLs
const fixImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  if (url.includes('emergentagent.com/api/') || url.includes('getmealguide.com/api/')) {
    const pathMatch = url.match(/\/api\/uploads\/.+$/);
    if (pathMatch) {
      return `${BACKEND_URL}${pathMatch[0]}`;
    }
  }
  return url;
};

export default function CoachMealPlanView({ mealPlan, clientId, onPlanUpdate }) {
  const [activeMealTab, setActiveMealTab] = useState(null);
  const [adjusting, setAdjusting] = useState(false);
  const [expandedRecipes, setExpandedRecipes] = useState({});

  if (!mealPlan || !mealPlan.recipes || mealPlan.recipes.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500">
          No meal plan data available
        </CardContent>
      </Card>
    );
  }

  // Group recipes by meal type - FILTER TO SHOW ONLY UNIQUE OPTIONS (like client view)
  // For snacks, split by meal_number to show Snack 1, Snack 2, Snack 3 separately
  const groupedRecipes = {};
  
  // First, deduplicate recipes by taking only unique recipe_ids per meal type + meal number combo
  mealPlan.recipes.forEach(recipe => {
    const mealType = recipe.meal_type || 'other';
    const mealNumber = recipe.meal_number || 1;
    
    // For snacks, create separate groups by meal number (Snack 1, Snack 2, Snack 3)
    let groupKey = mealType;
    if (mealType === 'snack' && mealNumber >= 4) {
      // Map meal_number to snack index: 4 -> snack_1, 5 -> snack_2, 6 -> snack_3
      const snackIndex = mealNumber - 3;
      groupKey = `snack_${snackIndex}`;
    }
    
    if (!groupedRecipes[groupKey]) {
      groupedRecipes[groupKey] = [];
    }
    
    // Check if we already have this recipe (by recipe_id or library_recipe_id)
    const recipeId = recipe.recipe_id || recipe.library_recipe_id;
    const alreadyExists = groupedRecipes[groupKey].some(r => 
      (r.recipe_id || r.library_recipe_id) === recipeId
    );
    
    // Only add if not duplicate and we have less than 3 options
    if (!alreadyExists && groupedRecipes[groupKey].length < 3) {
      groupedRecipes[groupKey].push(recipe);
    }
  });

  // Define meal order including separate snacks
  const mealOrder = ['breakfast', 'lunch', 'dinner', 'snack_1', 'snack_2', 'snack_3', 'snack'];
  const sortedMealTypes = Object.keys(groupedRecipes).sort((a, b) => {
    const aIndex = mealOrder.indexOf(a.toLowerCase());
    const bIndex = mealOrder.indexOf(b.toLowerCase());
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });

  // Calculate daily totals - Get FIRST unique recipe per meal_number (what client eats)
  // Since all recipes might be marked as option 1, we deduplicate by meal_number
  const uniqueRecipePerMeal = {};
  
  // Group by meal_number and take first unique recipe
  mealPlan.recipes.forEach(r => {
    const mealNum = r.meal_number || 1;
    if (!uniqueRecipePerMeal[mealNum]) {
      uniqueRecipePerMeal[mealNum] = r;
    }
  });
  
  const dailyRecipes = Object.values(uniqueRecipePerMeal);
  const dailyCalories = dailyRecipes.reduce((sum, r) => sum + (r.total_kcal || 0), 0);
  const dailyProtein = dailyRecipes.reduce((sum, r) => sum + (r.total_protein || 0), 0);
  const dailyCarbs = dailyRecipes.reduce((sum, r) => sum + (r.total_carbs || 0), 0);
  const dailyFat = dailyRecipes.reduce((sum, r) => sum + (r.total_fat || 0), 0);

  const handleTierAdjust = async (mealIdentifier, direction) => {
    setAdjusting(true);
    try {
      // groupedRecipes keys snacks by DISPLAY index (snack_1/2/3 = 1st/2nd/3rd snack
      // slot), but the backend expects the raw meal_number-based identifier
      // (snack_4/5/6, since meal_number 1-3 are breakfast/lunch/dinner). Without this
      // conversion, "Adjust Snack 1" would collide with meal_number=1 (breakfast).
      let backendMealIdentifier = mealIdentifier;
      if (mealIdentifier.startsWith('snack_')) {
        const displayIndex = parseInt(mealIdentifier.split('_')[1], 10);
        backendMealIdentifier = `snack_${displayIndex + 3}`;
      }

      // Use axios like the rest of the app - no token issues
      const response = await axios.post(
        `${API}/meal-plans/${clientId}/adjust-meal-tier`,
        { 
          meal_identifier: backendMealIdentifier,
          direction: direction
        }
      );

      const data = response.data;
      
      const optionsText = data.options_updated > 1 ? `all ${data.options_updated} options` : 'recipe';
      
      toast.success(
        `${formatMealType(mealIdentifier)} ${optionsText} adjusted to tier ${data.new_tier} (${data.calorie_change > 0 ? '+' : ''}${Math.round(data.calorie_change)} kcal)`,
        { duration: 4000 }
      );
      
      // Refresh meal plan
      if (onPlanUpdate) {
        await onPlanUpdate();
      }
    } catch (error) {
      console.error('Tier adjustment error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to adjust meal tier';
      toast.error(errorMsg);
    } finally {
      setAdjusting(false);
    }
  };

  const toggleRecipeExpand = (recipeId) => {
    setExpandedRecipes(prev => ({
      ...prev,
      [recipeId]: !prev[recipeId]
    }));
  };

  // Format meal type for display
  const formatMealType = (type) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="space-y-4">
      {/* Daily Totals */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Daily Nutrition</CardTitle>
          <CardDescription>Client's daily targets (Option 1 recipes)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xs text-gray-600 mb-1">Calories</p>
              <p className="text-xl font-bold text-blue-600">{Math.round(dailyCalories)}</p>
              <p className="text-xs text-gray-500">kcal</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-xs text-gray-600 mb-1">Protein</p>
              <p className="text-xl font-bold text-green-600">{Math.round(dailyProtein)}</p>
              <p className="text-xs text-gray-500">g</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg text-center">
              <p className="text-xs text-gray-600 mb-1">Carbs</p>
              <p className="text-xl font-bold text-orange-600">{Math.round(dailyCarbs)}</p>
              <p className="text-xs text-gray-500">g</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg text-center">
              <p className="text-xs text-gray-600 mb-1">Fat</p>
              <p className="text-xl font-bold text-purple-600">{Math.round(dailyFat)}</p>
              <p className="text-xs text-gray-500">g</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Meal Type Tabs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Meal Plan</CardTitle>
              <CardDescription>Click meal type to view options & adjust tiers</CardDescription>
            </div>
            <Badge variant="outline" className="ml-2">
              {sortedMealTypes.length} meals
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* Meal Tabs - Horizontal on desktop, wrap on mobile */}
          <div className="flex flex-wrap gap-2 mb-4">
            {sortedMealTypes.map(mealType => {
              const recipes = groupedRecipes[mealType];
              const isActive = activeMealTab === mealType;
              
              return (
                <Button
                  key={mealType}
                  data-testid={`meal-tab-${mealType}`}
                  onClick={() => setActiveMealTab(isActive ? null : mealType)}
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  className="flex-shrink-0"
                >
                  {formatMealType(mealType)}
                  <Badge variant="secondary" className="ml-2">
                    {recipes.length}
                  </Badge>
                </Button>
              );
            })}
          </div>

          {/* Active Meal Content */}
          {activeMealTab && groupedRecipes[activeMealTab] && (
            <div className="space-y-4 border-t pt-4">
              {/* Tier Adjustment Controls */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div>
                  <p className="font-semibold text-sm text-gray-900">Adjust {formatMealType(activeMealTab)} Tier</p>
                  <p className="text-xs text-gray-600">Change calorie tier for this meal type</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`tier-down-${activeMealTab}`}
                    onClick={() => handleTierAdjust(activeMealTab, 'down')}
                    disabled={adjusting}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <ChevronDown className="h-4 w-4 mr-1" />
                    -1 Tier
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`tier-up-${activeMealTab}`}
                    onClick={() => handleTierAdjust(activeMealTab, 'up')}
                    disabled={adjusting}
                    className="border-green-300 text-green-600 hover:bg-green-50"
                  >
                    <ChevronUp className="h-4 w-4 mr-1" />
                    +1 Tier
                  </Button>
                </div>
              </div>

              {/* Recipe Options */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-700">
                  {groupedRecipes[activeMealTab].length} Recipe Options:
                </p>
                {groupedRecipes[activeMealTab].map((recipe, idx) => (
                  <div
                    key={recipe.id}
                    data-testid={`recipe-option-${activeMealTab}-${idx}`}
                    className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div
                      className="p-3 cursor-pointer bg-white hover:bg-gray-50"
                      onClick={() => toggleRecipeExpand(recipe.id)}
                    >
                      <div className="flex items-center gap-3">
                        {/* Recipe Image - Smaller */}
                        {recipe.image_url && (
                          <img
                            src={fixImageUrl(recipe.image_url)}
                            alt={recipe.recipe_name}
                            className="w-16 h-16 object-cover rounded flex-shrink-0"
                          />
                        )}
                        
                        {/* Recipe Info - Compact */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm text-gray-900 truncate" data-testid={`recipe-name-${activeMealTab}-${idx}`}>
                                {recipe.recipe_name || recipe.name || 'Unnamed Recipe'}
                              </h4>
                              <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
                                {recipe.cooking_time && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {recipe.cooking_time}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <Flame className="h-3 w-3" />
                                  {Math.round(recipe.total_kcal || 0)} kcal
                                </span>
                              </div>
                            </div>
                            <Badge variant={idx === 0 ? 'default' : 'secondary'} className="text-xs">
                              Option {idx + 1}
                            </Badge>
                          </div>
                          
                          {/* Macros - Inline */}
                          <div className="flex gap-4 mt-2 text-xs">
                            <span className="text-blue-600">P: {Math.round(recipe.total_protein || 0)}g</span>
                            <span className="text-orange-600">C: {Math.round(recipe.total_carbs || 0)}g</span>
                            <span className="text-purple-600">F: {Math.round(recipe.total_fat || 0)}g</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedRecipes[recipe.id] && (
                      <div className="p-4 bg-gray-50 border-t text-sm space-y-3">
                        {/* Main Ingredients */}
                        {recipe.ingredients && recipe.ingredients.length > 0 && (
                          <div>
                            <p className="font-semibold text-gray-900 mb-2">Main Ingredients:</p>
                            <ul className="space-y-1 text-gray-700">
                              {recipe.ingredients
                                .filter(ing => {
                                  // Filter out cooking essentials from main ingredients
                                  const essentialIds = (recipe.cooking_essentials || []).map(e => e.food_bank_id);
                                  const ingId = ing.food_bank_id || ing.id;
                                  return !essentialIds.includes(ingId);
                                })
                                .map((ing, i) => {
                                  // Show EITHER display_text OR (quantity + name), not both
                                  const displayText = ing.display_text || 
                                    `${ing.grams || ing.quantity || ing.amount || 0}g ${ing.food_bank_name || ing.food_name || 'Unknown'}`;
                                  
                                  return (
                                    <li key={i} className="text-xs">
                                      • {displayText}
                                    </li>
                                  );
                                })}
                            </ul>
                          </div>
                        )}

                        {/* Cooking Essentials */}
                        {recipe.cooking_essentials && recipe.cooking_essentials.length > 0 && (
                          <div>
                            <p className="font-semibold text-gray-900 mb-2">Cooking Essentials:</p>
                            <ul className="space-y-1 text-gray-600 italic">
                              {recipe.cooking_essentials.map((essential, i) => {
                                // Find matching ingredient to get quantity
                                const matchingIng = recipe.ingredients?.find(
                                  ing => (ing.food_bank_id || ing.id) === essential.food_bank_id
                                );
                                
                                const displayText = matchingIng?.display_text || 
                                  essential.display_text || 
                                  essential.food_bank_name || 
                                  'Seasoning';
                                
                                return (
                                  <li key={i} className="text-xs">
                                    • {displayText}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}

                        {/* Instructions */}
                        {recipe.instructions && (
                          <div>
                            <p className="font-semibold text-gray-900 mb-2">Instructions:</p>
                            <ol className="space-y-2 text-gray-700">
                              {recipe.instructions.split(/\d+\./).filter(step => step.trim()).map((step, i) => (
                                <li key={i} className="text-xs flex">
                                  <span className="font-semibold mr-2">{i + 1}.</span>
                                  <span>{step.trim()}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
